/**
 * ICE Candidate Pair Selection Testing
 * Tests actual peer connection establishment and analyzes which candidate pair is selected
 */

export interface ICECandidateInfo {
  type: string;
  protocol: string;
  address: string;
  port: number;
  priority: number;
  foundation: string;
  relatedAddress?: string;
  relatedPort?: number;
}

export interface ICECandidatePairInfo {
  localCandidate: ICECandidateInfo;
  remoteCandidate: ICECandidateInfo;
  state: string;
  nominated: boolean;
  priority: number;
  bytesSent: number;
  bytesReceived: number;
  totalRoundTripTime: number;
  currentRoundTripTime: number;
  requestsSent: number;
  responsesReceived: number;
  connectionType: 'direct' | 'server-reflexive' | 'relay' | 'peer-reflexive' | 'unknown';
}

export interface ICETestResult {
  success: boolean;
  selectedPair: ICECandidatePairInfo | null;
  allPairs: ICECandidatePairInfo[];
  connectionEstablishmentTime: number;
  iceGatheringTime: number;
  iceConnectionState: string;
  iceGatheringState: string;
  error?: string;
}

/**
 * Determine connection type based on candidate types
 */
function determineConnectionType(localType: string, remoteType: string): ICECandidatePairInfo['connectionType'] {
  if (localType === 'host' && remoteType === 'host') {
    return 'direct';
  }
  if (localType === 'srflx' || remoteType === 'srflx') {
    return 'server-reflexive';
  }
  if (localType === 'relay' || remoteType === 'relay') {
    return 'relay';
  }
  if (localType === 'prflx' || remoteType === 'prflx') {
    return 'peer-reflexive';
  }
  return 'unknown';
}

/**
 * Parse ICE candidate string to extract information
 */
function parseCandidate(candidateString: string): Partial<ICECandidateInfo> {
  const parts = candidateString.split(' ');
  const candidate: Partial<ICECandidateInfo> = {};
  
  for (let i = 0; i < parts.length; i += 2) {
    const key = parts[i];
    const value = parts[i + 1];
    
    switch (key) {
      case 'typ':
        candidate.type = value;
        break;
      case 'raddr':
        candidate.relatedAddress = value;
        break;
      case 'rport':
        candidate.relatedPort = parseInt(value);
        break;
    }
  }
  
  // Parse the fixed positions
  if (parts.length >= 6) {
    candidate.foundation = parts[0];
    candidate.protocol = parts[2];
    candidate.priority = parseInt(parts[3]);
    candidate.address = parts[4];
    candidate.port = parseInt(parts[5]);
  }
  
  return candidate;
}

/**
 * Test ICE candidate pair selection by establishing a real peer connection
 */
export async function testICECandidatePairSelection(
  onProgress?: (progress: string) => void
): Promise<ICETestResult> {
  const startTime = Date.now();
  let iceGatheringCompleteTime: number | null = null;
  
  try {
    if (onProgress) onProgress('Creating peer connection...');
    
    // Create two peer connections to simulate a real call
    const pc1 = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun.cloudflare.com:3478' }
      ]
    });
    
    const pc2 = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun.cloudflare.com:3478' }
      ]
    });

    return new Promise((resolve) => {
      let resolved = false;
      let pc1GatheringComplete = false;
      let pc2GatheringComplete = false;
      
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          pc1.close();
          pc2.close();
          resolve({
            success: false,
            selectedPair: null,
            allPairs: [],
            connectionEstablishmentTime: Date.now() - startTime,
            iceGatheringTime: iceGatheringCompleteTime ? iceGatheringCompleteTime - startTime : 0,
            iceConnectionState: pc1.iceConnectionState,
            iceGatheringState: pc1.iceGatheringState,
            error: 'Connection timeout after 30 seconds'
          });
        }
      }, 30000);

      // Handle ICE gathering completion
      const checkGatheringComplete = () => {
        if (pc1GatheringComplete && pc2GatheringComplete && !iceGatheringCompleteTime) {
          iceGatheringCompleteTime = Date.now();
          if (onProgress) onProgress('ICE gathering complete, establishing connection...');
        }
      };

      pc1.onicegatheringstatechange = () => {
        if (pc1.iceGatheringState === 'complete') {
          pc1GatheringComplete = true;
          checkGatheringComplete();
        }
      };

      pc2.onicegatheringstatechange = () => {
        if (pc2.iceGatheringState === 'complete') {
          pc2GatheringComplete = true;
          checkGatheringComplete();
        }
      };

      // Handle ICE connection state changes
      pc1.oniceconnectionstatechange = async () => {
        console.log('PC1 ICE connection state:', pc1.iceConnectionState);
        
        if (pc1.iceConnectionState === 'connected' || pc1.iceConnectionState === 'completed') {
          if (!resolved) {
            resolved = true;
            clearTimeout(timeout);
            
            if (onProgress) onProgress('Analyzing selected candidate pair...');
            
            try {
              // Get WebRTC statistics
              const stats = await pc1.getStats();
              const candidatePairs: ICECandidatePairInfo[] = [];
              let selectedPair: ICECandidatePairInfo | null = null;
              
              // Parse candidate pair statistics
              for (const [id, stat] of stats) {
                if (stat.type === 'candidate-pair') {
                  const localStat = stats.get(stat.localCandidateId);
                  const remoteStat = stats.get(stat.remoteCandidateId);
                  
                  if (localStat && remoteStat) {
                    const localInfo: ICECandidateInfo = {
                      type: localStat.candidateType || 'unknown',
                      protocol: localStat.protocol || 'unknown',
                      address: localStat.address || 'unknown',
                      port: localStat.port || 0,
                      priority: localStat.priority || 0,
                      foundation: localStat.foundation || 'unknown',
                      relatedAddress: localStat.relatedAddress,
                      relatedPort: localStat.relatedPort
                    };
                    
                    const remoteInfo: ICECandidateInfo = {
                      type: remoteStat.candidateType || 'unknown',
                      protocol: remoteStat.protocol || 'unknown',
                      address: remoteStat.address || 'unknown',
                      port: remoteStat.port || 0,
                      priority: remoteStat.priority || 0,
                      foundation: remoteStat.foundation || 'unknown',
                      relatedAddress: remoteStat.relatedAddress,
                      relatedPort: remoteStat.relatedPort
                    };
                    
                    const pairInfo: ICECandidatePairInfo = {
                      localCandidate: localInfo,
                      remoteCandidate: remoteInfo,
                      state: stat.state || 'unknown',
                      nominated: stat.nominated || false,
                      priority: stat.priority || 0,
                      bytesSent: stat.bytesSent || 0,
                      bytesReceived: stat.bytesReceived || 0,
                      totalRoundTripTime: stat.totalRoundTripTime || 0,
                      currentRoundTripTime: stat.currentRoundTripTime || 0,
                      requestsSent: stat.requestsSent || 0,
                      responsesReceived: stat.responsesReceived || 0,
                      connectionType: determineConnectionType(localInfo.type, remoteInfo.type)
                    };
                    
                    candidatePairs.push(pairInfo);
                    
                    // The selected pair is typically the nominated one with state 'succeeded'
                    if ((stat.nominated && stat.state === 'succeeded') || 
                        (stat.state === 'succeeded' && !selectedPair)) {
                      selectedPair = pairInfo;
                    }
                  }
                }
              }
              
              pc1.close();
              pc2.close();
              
              resolve({
                success: true,
                selectedPair,
                allPairs: candidatePairs,
                connectionEstablishmentTime: Date.now() - startTime,
                iceGatheringTime: iceGatheringCompleteTime ? iceGatheringCompleteTime - startTime : 0,
                iceConnectionState: pc1.iceConnectionState,
                iceGatheringState: pc1.iceGatheringState
              });
              
            } catch (error) {
              pc1.close();
              pc2.close();
              resolve({
                success: false,
                selectedPair: null,
                allPairs: [],
                connectionEstablishmentTime: Date.now() - startTime,
                iceGatheringTime: iceGatheringCompleteTime ? iceGatheringCompleteTime - startTime : 0,
                iceConnectionState: pc1.iceConnectionState,
                iceGatheringState: pc1.iceGatheringState,
                error: `Failed to analyze stats: ${error instanceof Error ? error.message : 'Unknown error'}`
              });
            }
          }
        } else if (pc1.iceConnectionState === 'failed' || pc1.iceConnectionState === 'disconnected') {
          if (!resolved) {
            resolved = true;
            clearTimeout(timeout);
            pc1.close();
            pc2.close();
            resolve({
              success: false,
              selectedPair: null,
              allPairs: [],
              connectionEstablishmentTime: Date.now() - startTime,
              iceGatheringTime: iceGatheringCompleteTime ? iceGatheringCompleteTime - startTime : 0,
              iceConnectionState: pc1.iceConnectionState,
              iceGatheringState: pc1.iceGatheringState,
              error: `ICE connection ${pc1.iceConnectionState}`
            });
          }
        }
      };

      // Set up the connection
      if (onProgress) onProgress('Setting up peer connection...');
      
      // Add data channels to trigger ICE gathering
      pc1.createDataChannel('test');
      pc2.ondatachannel = () => {};
      
      // Handle ICE candidates
      pc1.onicecandidate = (event) => {
        if (event.candidate) {
          pc2.addIceCandidate(event.candidate);
        }
      };
      
      pc2.onicecandidate = (event) => {
        if (event.candidate) {
          pc1.addIceCandidate(event.candidate);
        }
      };
      
      // Create offer and set up the connection
      pc1.createOffer().then(offer => {
        pc1.setLocalDescription(offer);
        pc2.setRemoteDescription(offer);
        return pc2.createAnswer();
      }).then(answer => {
        pc2.setLocalDescription(answer);
        pc1.setRemoteDescription(answer);
        if (onProgress) onProgress('Negotiating connection...');
      }).catch(error => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeout);
          pc1.close();
          pc2.close();
          resolve({
            success: false,
            selectedPair: null,
            allPairs: [],
            connectionEstablishmentTime: Date.now() - startTime,
            iceGatheringTime: iceGatheringCompleteTime ? iceGatheringCompleteTime - startTime : 0,
            iceConnectionState: pc1.iceConnectionState,
            iceGatheringState: pc1.iceGatheringState,
            error: `SDP negotiation failed: ${error.message}`
          });
        }
      });
    });
    
  } catch (error) {
    return {
      success: false,
      selectedPair: null,
      allPairs: [],
      connectionEstablishmentTime: Date.now() - startTime,
      iceGatheringTime: 0,
      iceConnectionState: 'failed',
      iceGatheringState: 'complete',
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Get user-friendly description of connection type
 */
export function getConnectionTypeDescription(connectionType: ICECandidatePairInfo['connectionType']): string {
  switch (connectionType) {
    case 'direct':
      return 'Direct (Host-to-Host)';
    case 'server-reflexive':
      return 'Server Reflexive (Through NAT)';
    case 'relay':
      return 'Relay (Through TURN Server)';
    case 'peer-reflexive':
      return 'Peer Reflexive (Direct NAT Discovery)';
    case 'unknown':
    default:
      return 'Unknown';
  }
}

/**
 * Get performance assessment of the connection type
 */
export function getConnectionQualityAssessment(connectionType: ICECandidatePairInfo['connectionType']): {
  quality: 'excellent' | 'good' | 'fair' | 'poor';
  description: string;
} {
  switch (connectionType) {
    case 'direct':
      return {
        quality: 'excellent',
        description: 'Optimal direct connection with lowest latency'
      };
    case 'server-reflexive':
      return {
        quality: 'good',
        description: 'Good connection through NAT with minimal overhead'
      };
    case 'peer-reflexive':
      return {
        quality: 'fair',
        description: 'Acceptable connection with moderate latency'
      };
    case 'relay':
      return {
        quality: 'poor',
        description: 'Connection requires relay server, higher latency expected'
      };
    case 'unknown':
    default:
      return {
        quality: 'fair',
        description: 'Connection type could not be determined'
      };
  }
}
